import { useCallback, useEffect, useState } from "react";

import { EmbedTheme } from "@hl/shared-features/lib/features/mint/types";
import { ColorInput, TextInput, Stack, Anchor, Text } from "@mantine/core";
import { useInputState } from "@mantine/hooks";
import { debounce } from "lodash";

const defaultTheme: EmbedTheme = {
  bgColor: "#ffffff",
  textColor: "#000000",
  accentColor: "#212121",
};

const EMBED_DOCS_URL =
  "https://support.highlight.xyz/knowledge-base/for-creators/selling-your-nfts/sell-on-your-own-site-using-embeds#additional-customization-options";

type Props = {
  onThemeChange: (theme: EmbedTheme | null) => void;
};
export const EmbedThemeEditor = ({ onThemeChange }: Props) => {
  const [bgColor, setBackgroundColor] = useState<string>(defaultTheme.bgColor);
  const [textColor, setTextColor] = useState<string>(defaultTheme.textColor);
  const [accentColor, setAccentColor] = useState<string>(
    defaultTheme.accentColor
  );
  const [fontFamily, setFontFamily] = useInputState<string | null>(null);

  const debouncedOnThemeChange = useCallback(debounce(onThemeChange, 500), [
    onThemeChange,
  ]);

  useEffect(() => {
    const theme =
      !fontFamily &&
      bgColor === defaultTheme.bgColor &&
      textColor === defaultTheme.textColor
        ? null
        : {
            bgColor,
            textColor,
            accentColor,
            fontFamily: fontFamily || undefined,
          };
    debouncedOnThemeChange(theme);
  }, [bgColor, textColor, accentColor, fontFamily, debouncedOnThemeChange]);

  return (
    <Stack w="100%" pt="md">
      <ColorInput
        label="Background color"
        defaultValue={bgColor}
        onChange={setBackgroundColor}
      />
      <ColorInput
        label="Text color"
        defaultValue={textColor}
        onChange={setTextColor}
      />
      <ColorInput
        label="Accent color"
        defaultValue={accentColor}
        onChange={setAccentColor}
      />
      <TextInput
        label="Font family"
        placeholder="Specify font family from your site’s CSS"
        value={fontFamily ?? ""}
        onChange={setFontFamily}
      />
      <Text>
        Learn about{" "}
        <Anchor href={EMBED_DOCS_URL} fw={500} target="_blank">
          additional customization options
        </Anchor>
      </Text>
    </Stack>
  );
};
